import { projectsReportingProjectIndexCapability } from '@crest/module-projects/contracts/reporting-project-index';
import { defineCrestModule } from '@crest/platform-contracts/modules';
import { platformOptionalActionProviderHostCapability, platformOptionalFactProviderHostCapability, } from '@crest/platform-contracts/hosts';
import { reportingDefinitionsCapability, reportingExecutionCapability, reportingFinanceProjectPerformanceCapability, reportingTrainingTeamSummaryCapability, } from './contracts/reporting-definition.contract.js';
import { reportingCsvExportCapability } from './contracts/reporting-csv-export.contract.js';
import { reportingScheduleRunsCapability, reportingSchedulesCapability, } from './contracts/reporting-schedule.contract.js';
export const reportingModuleManifest = defineCrestModule({
    id: 'reporting', version: '1.0.0', kind: 'business', required: false,
    requires: ['platform', 'projects'],
    requiredCapabilities: [
        'platform.tenant-context.v1', 'platform.module-access.v1', 'platform.membership-directory.v1',
        platformOptionalActionProviderHostCapability, platformOptionalFactProviderHostCapability,
        projectsReportingProjectIndexCapability,
    ],
    optionalCapabilities: [
        'finance.reporting-project-financials.v1',
        'training.reporting-team-summary.v1',
        'communications.internal-report-delivery.v1',
    ],
    optionalCapabilityFallbacks: [{
            capability: 'finance.reporting-project-financials.v1',
            degradedMode: 'reporting.degraded.finance-project-performance-unavailable.v1',
        }, {
            capability: 'training.reporting-team-summary.v1',
            degradedMode: 'reporting.degraded.training-team-summary-unavailable.v1',
        }, {
            capability: 'communications.internal-report-delivery.v1',
            degradedMode: 'reporting.degraded.scheduled-delivery-suppressed.v1',
        }],
    provides: [
        reportingDefinitionsCapability, reportingExecutionCapability,
        reportingFinanceProjectPerformanceCapability, reportingCsvExportCapability,
        reportingTrainingTeamSummaryCapability,
        reportingSchedulesCapability, reportingScheduleRunsCapability,
    ],
    permissions: [
        'reporting.definitions.read', 'reporting.definitions.manage',
        'reporting.definitions.publish', 'reporting.execute', 'reporting.exports.csv',
        'reporting.schedules.read', 'reporting.schedules.manage',
    ],
    settings: [
        'reporting.definitions.configuration', 'reporting.definitions.display',
        'reporting.copy.definitions', 'reporting.schedules.configuration',
        'reporting.copy.schedules',
    ],
    copyCatalogs: ['reporting.copy.definitions', 'reporting.copy.schedules'],
    events: {
        publishes: [
            'reporting.definition-created.v1', 'reporting.definition-version-created.v1',
            'reporting.definition-published.v1', 'reporting.definition-archived.v1',
            'reporting.definition-rolled-back.v1',
            'reporting.schedule-created.v1', 'reporting.schedule-version-created.v1',
            'reporting.schedule-paused.v1', 'reporting.schedule-resumed.v1',
            'reporting.schedule-archived.v1', 'reporting.schedule-run-completed.v1',
            'reporting.schedule-run-suppressed.v1',
        ],
        subscribes: [],
    },
    server: {
        routes: [
            { id: 'reporting.routes.definitions', priority: 0 },
            { id: 'reporting.routes.schedules', priority: 10 },
        ],
        jobs: [
            { id: 'reporting.jobs.prepare-scheduled-csv', priority: 10 },
            { id: 'reporting.jobs.purge-expired-schedule-artifacts', priority: 20 },
        ],
        subscribers: [], slots: [],
    },
    web: {
        routes: [{ id: 'reporting.web.definitions', priority: 0 }],
        navigation: [{ id: 'reporting.navigation.definitions', priority: 85 }], slots: [],
    },
    mobile: { routes: [], slots: [] }, portal: { routes: [], slots: [] },
    database: {
        schema: 'reporting',
        owns: [
            'reporting.definitions', 'reporting.definition_versions',
            'reporting.definition_events', 'reporting.definition_commands',
            'reporting.execute_definition_command',
            'reporting.schedules', 'reporting.schedule_versions', 'reporting.schedule_events',
            'reporting.schedule_commands', 'reporting.schedule_runs',
            'reporting.schedule_run_artifacts', 'reporting.execute_schedule_command',
            'reporting.claim_due_schedule_runs', 'reporting.prepare_schedule_run_artifact',
            'reporting.finish_claimed_schedule_run', 'reporting.purge_expired_schedule_run_artifacts',
        ],
        readsThrough: [
            'platform.membership-directory.v1',
            projectsReportingProjectIndexCapability,
            'finance.reporting-project-financials.v1',
            'training.reporting-team-summary.v1',
        ],
        migrations: [
            'reporting.definition-foundation', 'reporting.schedule-csv-foundation',
            'reporting.training-team-summary',
        ],
    },
    health: ['reporting.database'],
    lifecycle: {
        initialization: { timeoutMs: 5_000, failureBehavior: 'quarantine-module' },
        resources: {
            requestConcurrency: 32, jobConcurrency: 1, queueBacklogLimit: 100,
            databaseConnectionBudget: 10, workerIsolation: 'separate-worker',
        },
        disable: {
            data: 'retain', clientContributions: 'remove', serverRoutes: 'deny-tenant-access',
            scheduledJobs: 'skip',
            events: {
                behavior: 'drop-safe', durableCursor: false, retentionDays: 0, backlogLimit: 0,
                warningAt: 0, reconciliationProcedure: null,
                dropSafeReason: 'Schedule occurrences are skipped while disabled and are never delivered retroactively.',
            },
            outboundSideEffects: 'suppress-no-retroactive-send', inFlight: 'finish-idempotently',
        },
        resume: {
            configuration: 'last-compatible-published', healthChecks: ['reporting.database'],
            reconciliationProcedure: null, visibility: 'after-health-and-reconciliation',
        },
        uninstall: {
            executableCode: 'remove', data: 'retain', tombstone: 'retain-immutable',
            destruction: 'separate-explicit-authority',
        },
    },
    retentionPolicy: 'Reporting metadata remains stored while disabled; schedule CSV bytes expire and purge under tenant policy.',
});
//# sourceMappingURL=reporting.module.js.map